import { i18n } from "../i18n/i18n";

import React, { Component, Fragment } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { find, isEqual, map, reject, uniqueId } from 'lodash'
import { produce } from 'immer'
import Row from './Row/Row'
import InputText from './InputText'
import Button from './Button'
import Heading from './Heading'
import TableHorizontal, { TableHorizontalData } from './TableHorizontal'
import { ActionName } from './Actions'

export interface ContactSiteInputSite {
  id: number
  name: string
  address: string
  city: string
  state: string
  zip: string
}

interface ContactSiteInputProps extends RouteComponentProps {
  sitesExisting?: ContactSiteInputSite[]
  sitesCreated: ContactSiteInputSite[]
  sitesUpdated?: ContactSiteInputSite[]
  sitesDeleted?: ContactSiteInputSite[]
  onSitesCreatedChange: (sitesCreated: ContactSiteInputSite[]) => void
  onSitesUpdatedChange?: (sitesUpdated: ContactSiteInputSite[]) => void
  onSitesDeletedChange?: (sitesDeleted: ContactSiteInputSite[]) => void
}

interface ContactSiteInputState {
  sites: ContactSiteInputSite[]
  name: string
  address: string
  city: string
  state: string
  zip: string
}

class ContactSiteInput
  extends Component<ContactSiteInputProps, ContactSiteInputState> {
  mounted: boolean = false

  componentDidUpdate (
    prevProps: ContactSiteInputProps,
    prevState: ContactSiteInputState
  ) {
    const { sitesExisting } = this.props
    if (!this.mounted && !isEqual(sitesExisting, prevState.sites)) {
      if (sitesExisting) {
        this.mounted = true
        this.setState({ sites: sitesExisting })
      }
    }
  }

  headers = [
    { heading: i18n("Site name"), path: 'name' },
    { heading: i18n("Address"), path: 'address' },
    { heading: i18n("City"), path: 'city' },
    { heading: i18n("State"), path: 'state' },
    { heading: i18n("Zip"), path: 'zip' },
    { heading: i18n("Actions") }
  ]

  constructor (props: ContactSiteInputProps) {
    super(props)
    this.state = {
      sites: [],
      name: '',
      address: '',
      city: '',
      state: '',
      zip: ''
    }
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleActionsClick = this.handleActionsClick.bind(this)
    this.handleAddClick = this.handleAddClick.bind(this)
  }

  get tableData (): TableHorizontalData<ContactSiteInputSite>[] {
    return map(this.state.sites, site => {
      return { ...site, actions: ['delete'] }
    })
  }

  get site () {
    const { name, address, city, state, zip } = this.state
    const hasEmptyFields = !name || !address || !city || !state || !zip
    if (hasEmptyFields) return
    const id = Number(uniqueId())
    return { id, name, address, city, state, zip }
  }

  addToSites (site: ContactSiteInputSite) {
    const sites = produce(this.state.sites, sites => {
      sites = reject(sites, { name: site.name })
      sites.push(site)
      return sites
    })
    this.setState({ sites })
  }

  addSitesCreated (site: ContactSiteInputSite) {
    const siteExisting = find(this.props.sitesExisting, { name: site.name })
    if (!siteExisting) {
      const sitesCreated = produce(this.props.sitesCreated, sitesCreated => {
        sitesCreated = reject(sitesCreated, { name: site.name })
        sitesCreated.push(site)
        return sitesCreated
      })
      this.props.onSitesCreatedChange(sitesCreated)
    }
  }

  addSitesUpdated (site: ContactSiteInputSite) {
    const { onSitesUpdatedChange, sitesUpdated, sitesExisting } = this.props
    const siteExisting = find(sitesExisting, { name: site.name })
    if (onSitesUpdatedChange && sitesUpdated && siteExisting) {
      const updated = produce(sitesUpdated, sitesUpdated => {
        sitesUpdated = reject(sitesUpdated, { name: siteExisting.name })
        site.id = siteExisting.id // Local id to resource id
        sitesUpdated.push(site)
        return sitesUpdated
      })
      onSitesUpdatedChange(updated)
    }
  }

  addSitesDeleted (id: number) {
    const { onSitesDeletedChange, sitesDeleted } = this.props
    if (sitesDeleted && onSitesDeletedChange) {
      const siteExisting = find(this.props.sitesExisting, { id })
      if (siteExisting) {
        const deleted = produce(sitesDeleted, sitesDeleted => {
          sitesDeleted = reject(sitesDeleted, { id })
          sitesDeleted.push(siteExisting)
          return sitesDeleted
        })
        onSitesDeletedChange(deleted)
      }
    }
  }

  removeFromSites (id: number) {
    const sites = produce(this.state.sites, sites => reject(sites, { id }))
    this.setState({ sites })
  }

  removeSitesCreated (id: number) {
    const sitesCreated = produce(this.props.sitesCreated, sitesCreated => {
      return reject(sitesCreated, { id })
    })
    this.props.onSitesCreatedChange(sitesCreated)
  }

  removeSitesUpdated (id: number) {
    const { onSitesUpdatedChange, sitesUpdated } = this.props
    if (onSitesUpdatedChange && sitesUpdated) {
      const updated = produce(sitesUpdated, sitesUpdated => {
        return reject(sitesUpdated, { id })
      })
      if (onSitesUpdatedChange) onSitesUpdatedChange(updated)
    }
  }

  resetFields () {
    const defaultValues = {
      name: '',
      address: '',
      city: '',
      state: '',
      zip: ''
    }
    this.setState(defaultValues)
  }

  handleInputChange (name: string, value: string) {
    if (name === 'name') this.setState({ name: value })
    else if (name === 'address') this.setState({ address: value })
    else if (name === 'city') this.setState({ city: value })
    else if (name === 'state') this.setState({ state: value })
    else if (name === 'zip') this.setState({ zip: value })
  }

  handleActionsClick (id: number, action: ActionName) {
    if (action === 'delete') {
      this.removeSitesCreated(id)
      this.removeSitesUpdated(id)
      this.removeFromSites(id)
      this.addSitesDeleted(id)
    }
  }

  handleAddClick () {
    const { site } = this
    if (site) {
      this.addSitesCreated(site)
      this.addSitesUpdated(site)
      this.addToSites(site)
      this.resetFields()
    }
  }

  render () {
    return (
      <Fragment>
        <Heading text={i18n("Contact sites")} className="mb-3" />
        <TableHorizontal
          headers={this.headers}
          data={this.tableData }
          onActionClick={this.handleActionsClick} />
        <Row column={2}>
          <InputText
            name="name"
            value={this.state.name}
            label={i18n("Site name")}
            onChange={this.handleInputChange} />
          <InputText
            name="address"
            value={this.state.address}
            label={i18n("Address")}
            onChange={this.handleInputChange} />
          <InputText
            name="city"
            value={this.state.city}
            label={i18n("City")}
            onChange={this.handleInputChange} />
          <InputText
            name="state"
            value={this.state.state}
            label={i18n("State")}
            onChange={this.handleInputChange} />
          <InputText
            name="zip"
            value={this.state.zip}
            label={i18n("Zip")}
            onChange={this.handleInputChange} />
          <div className="flex items-end mb-4">
            <Button
              text={i18n("Add")}
              full={true}
              onClick={this.handleAddClick} />
          </div>
        </Row>
      </Fragment>
    )
  }
}

export default withRouter(ContactSiteInput)
